import { useMemo, useState, useEffect, useCallback } from 'react';

import { Box, Stack } from '@mui/system';
import Grid from '@mui/system/Unstable_Grid';
import { Tab, Card, Tabs, Alert, Switch, Typography, CardHeader, CardContent, FormControlLabel } from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import PostCommentForm from 'src/layouts/common/threads/post-comment-form';
import PostCommentList from 'src/layouts/common/threads/post-comment-list';
import { ThreadVisibility } from 'src/services/threads/threads.types';
import { useGetCandidateProfileQuery } from 'src/services/candidates/candidates.service';
import { useGetApplicationActivitiesQuery } from 'src/services/applications/applications.service';
import {
  JobApplication,
  ApplicationStage,
  RejectionContext,
} from 'src/services/applications/applications.types';

import Iconify from 'src/components/iconify';

import ActivityListCard from 'src/sections/activity/card/activity-feed';
import { ApplicationInfoCard } from 'src/sections/jobs/applications/cards/info-card';
import RejectionContextAlert from 'src/sections/jobs/applications/alerts/rejected-alert';
import CandidateProfileEditor from 'src/sections/profile/profile-sections/profile-editor';

import { TenantType } from 'src/types/enums';

import PendingActionsCard from '../cards/pending-actions.card';
import { ApplicationInsightsCard } from '../cards/insights-card';
import UpcomingInterviewCard from '../cards/upcoming-interview-card';

type Props = {
  application: JobApplication;
  profileId: string;
  internalThreadId?: string;
  managerCandidateThreadId?: string;
  managerClientThreadId?: string;
  /**
   * PRIVATE means the agency is the only organisation on the Employer Chat — it exists because a
   * guest left a note on a job that has no client organisation. Nobody receives a reply posted
   * there, so the composer is closed rather than quietly swallowing one.
   */
  managerClientThreadVisibility?: ThreadVisibility;
  noteId?: string;
};

export default function ApplicationCandidateProfile({
  application,
  profileId,
  internalThreadId,
  managerCandidateThreadId,
  managerClientThreadId,
  managerClientThreadVisibility,
  noteId,
}: Props) {

  const { currentData: profile, isLoading } = useGetCandidateProfileQuery(profileId as string, {
    skip: !profileId
  });

  const { t } = useTranslate();

  const dispatch = useAppDispatch();

  const tenantType = useOrgTenant();

  const [currentActivitiesPage, setCurrentActivitiesPage] = useState<number>(1);

  const canEdit = useMemo(() => {

    if (!application) return false;

    if (tenantType === TenantType.Candidate) {
      return false;
    }

    if (application.manager_type === TenantType.Recruiter) {
      if (application.sent_to_client_at) {
        return false;
      }
    }

    if ([
      ApplicationStage.WITHDRAWN,
      ApplicationStage.REJECTED,
      ApplicationStage.OFFER_ACCEPTED
    ].includes(application.stage)) {
      return false;
    }

    return true;

  }, [application, tenantType]);

  // Merged by default: the point of the feed is that the candidate's history is there without
  // anyone having to go looking for it.
  const [showProfileNotes, setShowProfileNotes] = useState<boolean>(true);

  const [selectedThreadTab, setSelectedThreadTab] = useState<string | null>('');

  useEffect(() => {
    if (managerCandidateThreadId) {
      setSelectedThreadTab('manager_candidate');
    }
    else if (managerClientThreadId) {
      setSelectedThreadTab('manager_client');
    }
    else {
      setSelectedThreadTab(null);
    }
  }, [managerCandidateThreadId, managerClientThreadId]);

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setSelectedThreadTab(newValue);
  }, []);

  const { currentData: activities } = useGetApplicationActivitiesQuery(
    {
      applicationId: application?.id as string,
      page: currentActivitiesPage,
      perPage: 5,
    },
    {
      skip: !application?.id,
    }
  );

  // all applications
  // rejects -> candidate can't communicate back
  // if candidate withdraws, agency/client can't communicate back
  const isCandidateManagerThreadLocked = useMemo(() => {
    if (!application) return false;

    if (application.stage === ApplicationStage.WITHDRAWN) {
      if (tenantType !== TenantType.Candidate) {
        return true;
      }
    } else if (application.stage === ApplicationStage.REJECTED) {
      if (tenantType === TenantType.Candidate) {
        return true;
      }
    }

    return false;
  }, [application, tenantType]);

  return (
    <Grid container spacing={3}>
      <Grid sm={12} md={8}>
        {tenantType === TenantType.Recruiter &&
          ![
            ApplicationStage.REJECTED,
            ApplicationStage.WITHDRAWN,
            ApplicationStage.OFFER_ACCEPTED,
          ].includes(application.stage) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {canEdit
                ? t('applications.detail.candidate_profile.subheader_not_shortlisted')
                : t('applications.detail.candidate_profile.subheader_shortlisted')}
            </Alert>
          )}

        {application.stage === ApplicationStage.REJECTED && (
          <RejectionContextAlert context={application.rejection_context as RejectionContext} />
        )}

        <CandidateProfileEditor
          profile={profile}
          isLoading={isLoading}
          mode={canEdit ? 'edit' : 'view'}
          commsEntityId={application.org_candidate?.id}
        />
      </Grid>
      <Grid sm={12} md={4}>
        <Stack direction="column" spacing={2}>

          <ApplicationInfoCard applicationId={application.id} />

          <PendingActionsCard applicationId={application.id} />

          <UpcomingInterviewCard applicationId={application.id} />

          <ApplicationInsightsCard applicationId={application.id} />

          <Card>
            <CardHeader
              title={
                <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                  <Iconify icon="uil:notes" />
                  <Box>{t('applications.detail.candidate_profile.notes')}</Box>
                </Stack>
              }
            />
            <CardContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t(
                  `applications.detail.candidate_profile.internal_thread.subheader_${tenantType}`
                )}
              </Alert>

              {/* Written to this application's thread either way — the toggle only changes what is
                  read back. Off, the list is the application's own notes and nothing else. */}
              <PostCommentForm thread_id={internalThreadId} />

              <Stack direction="row" justifyContent="flex-end">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showProfileNotes}
                      onChange={(_event, checked) => setShowProfileNotes(checked)}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t('applications.detail.candidate_profile.internal_thread.include_profile_notes')}
                    </Typography>
                  }
                />
              </Stack>

              {showProfileNotes
                ? <PostCommentList feed={{ type: 'application', id: application.id }} />
                : <PostCommentList thread_id={internalThreadId} />}
            </CardContent>
          </Card>

          {
            (managerCandidateThreadId || managerClientThreadId) && (
              <Card>
                <CardHeader
                  title={
                    <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                      <Iconify icon="mdi:comments-outline" />
                      <Box>{t('applications.detail.candidate_profile.comments')}</Box>
                    </Stack>
                  }
                />
                <CardContent>

                  {
                    selectedThreadTab && (
                      <Alert severity="info">
                        {t(
                          `applications.detail.candidate_profile.${selectedThreadTab}_thread.subheader_${tenantType}`
                        )}
                      </Alert>
                    )
                  }

                  <Box display="flex" justifyContent="center" width="100%">
                    <Tabs
                      sx={{ marginY: 2 }}
                      variant='scrollable'
                      value={selectedThreadTab}
                      onChange={handleChangeTab}
                    >
                      {managerCandidateThreadId && (
                        <Tab
                          key="manager_candidate"
                          iconPosition="end"
                          value="manager_candidate"
                          label={t(
                            `applications.detail.candidate_profile.manager_candidate_thread.${tenantType}_title`
                          )}
                        />
                      )}
                      {managerClientThreadId && (
                        <Tab
                          key="manager_client"
                          iconPosition="end"
                          value="manager_client"
                          label={t(
                            `applications.detail.candidate_profile.manager_client_thread.${tenantType}_title`
                          )}
                        />
                      )}
                    </Tabs>
                  </Box>

                  {selectedThreadTab === 'manager_candidate' && (
                    <>
                      <PostCommentForm
                        thread_id={managerCandidateThreadId}
                        disabled={isCandidateManagerThreadLocked}
                      />
                      <PostCommentList thread_id={managerCandidateThreadId} />
                    </>
                  )}

                  {selectedThreadTab === 'manager_client' && (
                    <>
                      {/* A guest cannot receive replies on this thread — there is no guest
                          thread-read endpoint at all. When the thread is PRIVATE the agency is its
                          only member, so a reply reaches nobody; say so and close the composer
                          rather than letting it be typed into a void (JT1-1491). */}
                      {managerClientThreadVisibility === ThreadVisibility.PRIVATE ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          {t('applications.detail.candidate_profile.manager_client_thread.guest_notes_only')}
                        </Alert>
                      ) : (
                        <PostCommentForm thread_id={managerClientThreadId} />
                      )}

                      <PostCommentList thread_id={managerClientThreadId} />
                    </>
                  )}
                </CardContent>
              </Card>
            )
          }

          <Card>
            <CardHeader title={t('activities.feed.title')} />
            <CardContent>
              <ActivityListCard
                activities={activities?.results ?? []}
                page={currentActivitiesPage}
                totalItems={activities?.count ?? 0}
                perPage={5}
                onPageChange={(e: React.ChangeEvent<unknown>, newPage: number) =>
                  setCurrentActivitiesPage(newPage)
                }
              />
            </CardContent>
          </Card>
        </Stack>
      </Grid>
    </Grid>
  );
}
